import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownLeft, Bell, Plus, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";
import Avatar from "../components/Avatar";
import PartnerBadge from "../components/PartnerBadge";

const formatDate = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const diff = (now - d) / 86400000;
  if (diff < 1 && d.getDate() === now.getDate()) return "Today";
  if (diff < 2) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

export const HomeScreen = () => {
  const nav = useNavigate();
  const { transactions, partner, resetDraft, initialBalance } = useApp();
  const totalSent = transactions
    .filter((t) => t.status === "COMPLETED")
    .reduce((s, t) => s + t.amount, 0);
  const balance = initialBalance - totalSent;

  const recent = transactions.slice(0, 5);

  const startFlow = () => {
    resetDraft();
    nav("/send/recipient");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-full flex flex-col pb-32"
      data-testid="home-screen"
    >
      {/* Header */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar initials="AV" color="#FDE68A" size={40} onClick={() => nav("/profile")} />
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
              Ready to
            </div>
            <div className="font-display text-lg font-bold text-gray-900 dark:text-white leading-none">
              Shwiip
            </div>
          </div>
        </div>
        <button
          data-testid="notifications-btn"
          onClick={() => nav("/notifications")}
          className="w-10 h-10 rounded-full bg-gray-50 dark:bg-white/10 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/20 transition-colors relative"
        >
          <Bell className="w-5 h-5 text-gray-900 dark:text-white" strokeWidth={2} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-500" />
        </button>
      </div>

      {/* Hero card */}
      <div className="px-6 mt-4">
        <div className="relative overflow-hidden rounded-3xl bg-gray-900 text-white p-6 pb-7">
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/5" />
          <div className="absolute top-12 -right-6 w-24 h-24 rounded-full bg-white/5" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
              Payment Orchestration
            </div>
            <div className="font-display text-[32px] leading-[1.05] font-bold mt-1.5">
              Send money
              <br />
              worldwide.
            </div>
            <div className="text-[13px] text-gray-400 mt-3 leading-snug max-w-[260px]">
              Shwiip connects you to licensed partners. You send, {partner.name} executes.
            </div>
            <button
              data-testid="send-money-btn"
              onClick={startFlow}
              className="mt-5 inline-flex items-center gap-2 bg-white text-gray-900 h-12 px-5 rounded-full font-semibold text-[15px] hover:bg-gray-100 active:scale-[0.98] transition-all"
            >
              <Plus className="w-4 h-4" strokeWidth={3} />
              Shwiip Money
            </button>
          </div>
        </div>
      </div>

      {/* Partner attribution strip */}
      <div className="px-6 mt-4">
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 text-gray-600" strokeWidth={2.5} />
          <span>
            All payments executed by{" "}
            <span className="font-semibold text-gray-800">{partner.name}</span> — a
            licensed PSP.
          </span>
        </div>
      </div>

      {/* Quick contacts / Recent */}
      <div className="px-6 mt-6">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-lg font-bold tracking-tight dark:text-white">
            Recent activity
          </h2>
          <button
            data-testid="see-all-activity-btn"
            onClick={() => nav("/activity")}
            className="text-xs font-semibold text-gray-500 hover:text-gray-900"
          >
            See all
          </button>
        </div>

        {recent.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center">
            <div className="text-sm font-semibold text-gray-700">No transactions yet</div>
            <div className="text-xs text-gray-500 mt-1">
              Tap <span className="font-semibold">Shwiip Money</span> to send your first
              payment.
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {recent.map((tx) => (
              <button
                key={tx.id}
                data-testid={`home-tx-${tx.id}`}
                onClick={() => nav(`/transaction/${tx.id}`)}
                className="w-full flex items-center gap-3 py-3 hover:bg-gray-50 dark:hover:bg-white/5 rounded-2xl px-2 -mx-2 transition-colors text-left"
              >
                <Avatar
                  initials={tx.recipient?.initials}
                  color={tx.recipient?.color}
                  size={42}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-[14px] text-gray-900 dark:text-white truncate">
                      {tx.recipient?.name}
                    </div>
                    {tx.status === "FAILED" && (
                      <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                        FAILED
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {formatDate(tx.createdAt)} · Executed by {tx.partner}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-gray-900 dark:text-white font-semibold text-[14px]">
                  <ArrowUpRight className="w-3.5 h-3.5 text-gray-400" strokeWidth={2.5} />
                  {tx.amount.toFixed(2)} kr
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default HomeScreen;
